import { createContext, useContext, useEffect, useState } from "react";
import { FormProvider, UseFormReturn } from "react-hook-form";
import { Box } from "@mui/material";
import { Outlet, useNavigate } from "react-router-dom";
import { MarathonNav } from "./marathonNav/MarathonNav";
import MarathonCreatedModal from "../../../entities/admin/ui/MarathonCreatedModal/MarathonCreatedModal";
import { ICreateMarathon } from "features/createEditMarathon/model/types";
import { EditorState, ContentState, convertFromHTML } from "draft-js";
import { useMarathonForm } from "../model/hooks/useMarathonForm";
import { removeFromLocal } from "shared/services/getLocal";
import { Dayjs } from "dayjs";

interface MarathonFormEditProps {
  data?: any;
  isEditMode?: boolean;
}

interface MarathonFormContext {
  form: UseFormReturn<ICreateMarathon>;
  handleCreateMarathon: (data: ICreateMarathon) => void;
  setCreatedMarathonModalOpen: (value: boolean) => void;
  isCreatedMarathonModalOpen: boolean;
}

export const FormContext = createContext<UseFormReturn<ICreateMarathon> | null>(
  null
);

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error("useFormContext must be used within a FormProvider");
  }
  return context;
};

export const MarathonForm = ({ data, isEditMode }: MarathonFormEditProps) => {
  const navigate = useNavigate();
  const [isCreatedMarathonModalOpen, setCreatedMarathonModalOpen] =
    useState<boolean>(false);

  const { form, handleCreateMarathon, handleUpdateMarathon } = useMarathonForm(
    setCreatedMarathonModalOpen,
    isEditMode ?? false,
    data
  );

  const { setValue } = form;

  const dateEvent = form.watch("dateEvent");

  useEffect(() => {
    removeFromLocal("startNumberRanges_distance");

    const createEditorStateFromHTML = (html: string | null) => {
      if (!html) return EditorState.createEmpty();
      try {
        const blocksFromHTML = convertFromHTML(html);
        const contentState = ContentState.createFromBlockArray(
          blocksFromHTML.contentBlocks,
          blocksFromHTML.entityMap
        );
        return EditorState.createWithContent(contentState);
      } catch (e) {
        return EditorState.createEmpty();
      }
    };

    if (isEditMode && data?.translations) {
      const initialTranslations = data.translations.map((t: any) => ({
        logo: t.logo ?? null,
        name: t.name ?? "",
        text: createEditorStateFromHTML(t.text ?? null),
        place: t.place ?? "",
        languageId: t.languageId,
        startKitMessage: t.startKitMessage ?? "",
      }));
      setValue("translations", initialTranslations);
    }

    if (isEditMode && data?.documents) {
      const initialDocuments = data.documents.map((doc: any) => doc.document);
      setValue("documents", initialDocuments);
    }

    if (isEditMode && Array.isArray(data?.partners)) {
      const initialPartners = data.partners.map((partner: any) => ({
        id: partner.id,
        serialNumber: partner.serialNumber,
        translations: partner.translations ?? [],
        partnerCompanies:
          partner.partnerCompanies?.map((company: any) => ({
            id: company.id,
            name: company.name,
            url: company.url,
            logo: company.logo ?? null,
          })) ?? [],
      }));
      setValue("partners", initialPartners);
    }

    if (isEditMode && data?.publicOfferFiles) {
      const initialPublicOffer = data.publicOfferFiles.map((file: any) => ({
        file: file.documentPath,
        languageId: file.languageId,
      }));
      setValue("publicOffer", initialPublicOffer);
    }
  }, [isEditMode, data, setValue]);

  const outletContext = {
    form,
    handleCreateMarathon: isEditMode
      ? handleUpdateMarathon
      : handleCreateMarathon,
    setCreatedMarathonModalOpen,
    isCreatedMarathonModalOpen,
    dateEvent,
    insurancePrice: isEditMode ? data?.insurancePrice?.toString() ?? "" : "",
    setInsurancePrice: (val: string) => form.setValue("insurancePrice", val),
    distances: isEditMode ? data?.distances ?? [] : [],
    periodsError: null,
    data: isEditMode ? data : undefined,
  };

  const handleSubmit = async (formData: ICreateMarathon) => {
    try {
      if (isEditMode) {
        await handleUpdateMarathon(formData);
        navigate("/admin_panel/marathons");
      } else {
        await handleCreateMarathon(formData);
      }
    } catch (error) {
      console.error("Ошибка при создании/обновлении марафона:", error);
    }
  }

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        noValidate
      >
        <Box>
          <MarathonNav isEditMode={isEditMode} />
          <Outlet context={outletContext as MarathonFormContext} />
        </Box>
        <MarathonCreatedModal
          handleCreatedMarathonClose={() => setCreatedMarathonModalOpen(false)}
          isCreatedMarathonModalOpen={isCreatedMarathonModalOpen}
        />
      </form>
    </FormProvider>
  );
};
